import React from 'react'
import { Router as ReactRouter } from 'react-router'
import { Redirect, Route, Switch } from 'react-router-dom'
import { getHistory } from '@features/appRouting'
import { ImperativeModalProvider, modalManager } from '@features/imperativeModals'

import Login from '../Login/Login'
import { useHandleDockOpeningQueryStringParam } from './hooks'
import { isAllowedToSetFeaturesFromUrl } from '../../store/features/featuresService.general'
import {
  AnalyticsPage,
  BotPage,
  BotTesterScenariosPage,
  callsListUrlGetters,
  CallsRecFilePage,
  CallsTablePage,
  CampaignContactsSheetPage,
  ConversationsTablePage,
  FeatureSetterPage,
  HomePage,
  MarketplaceSignupPage,
  RecordedTesterRunPage,
  TeamManagementPage,
  VoicebuilderPage,
  WizardPage
} from './pages'

import { hasParam } from '../../lib/urlService'
import { WithAuth } from './WithAuth'
import { ChasingDotsLoader } from '../design-system/ChasingDotsLoader'
import { FeedbotSuspense } from '../design-system/FeedbotSuspense'
import { useCheckDeployedVersion } from '../Bot/hooks/useCheckDeployedVersion'

export const Router: React.FC = () => {
  useHandleDockOpeningQueryStringParam()
  useCheckDeployedVersion() // Needed for SDK features across tabs

  if (isAllowedToSetFeaturesFromUrl()) {
    return (
      <ReactRouter history={getHistory()} key="auth-page-router">
        <WithAuth botId={undefined}>
          <Route component={FeatureSetterPage} />
        </WithAuth>
      </ReactRouter>
    )
  }

  return (
    <FeedbotSuspense name="Router" fallback={<ChasingDotsLoader />}>
      <ReactRouter history={getHistory()} key="main-router">
        {/* Needs to be inside router - otherwise we would not be able to use routing-related hooks in modals */}
        <ImperativeModalProvider subscribe={modalManager.subscribe} />

        <Switch>
          <Route
            path="/analytics"
            render={(props: any) => (
              <WithAuth botId={undefined}>
                <AnalyticsPage fullscreen={hasParam(props.location, 'fullscreen')} />
              </WithAuth>
            )}
          />

          <Route
            path="/bots/:botId/analytics"
            render={(props: any) => (
              <WithAuth botId={props.match.params.botId}>
                <AnalyticsPage
                  botId={props.match.params.botId!}
                  fullscreen={hasParam(props.location, 'fullscreen')}
                />
              </WithAuth>
            )}
          />

          <Route
            path="/bots/:botId/calls/validator/step/:stepId"
            render={props => (
              <WithAuth botId={props.match.params.botId}>
                <CallsTablePage
                  sourceUrl={callsListUrlGetters.validatorResultsForStep(props.match.params as any)}
                  fullscreen={hasParam(props.location, 'fullscreen')}
                  botId={props.match.params.botId!}
                />
              </WithAuth>
            )}
          />

          <Route
            path="/bots/:botId/calls/validator/type/:validatorType"
            render={props => (
              <WithAuth botId={props.match.params.botId}>
                <CallsTablePage
                  sourceUrl={callsListUrlGetters.validatorResultsForType(props.match.params as any)}
                  fullscreen={hasParam(props.location, 'fullscreen')}
                  botId={props.match.params.botId!}
                />
              </WithAuth>
            )}
          />

          <Route
            path="/bots/:botId/calls/user/:userId/transcript"
            render={props => (
              <WithAuth botId={props.match.params.botId}>
                <CallsTablePage
                  sourceUrl={callsListUrlGetters.transcriptForUser(props.match.params as any)}
                  fullscreen={hasParam(props.location, 'fullscreen')}
                  botId={props.match.params.botId!}
                />{' '}
              </WithAuth>
            )}
          />

          <Route
            path="/bots/:botId/calls/user/:userId/rec-file"
            render={props => (
              <WithAuth botId={props.match.params.botId}>
                <CallsRecFilePage
                  botId={props.match.params.botId!}
                  userId={props.match.params.userId!}
                />
              </WithAuth>
            )}
          />

          <Route
            path="/bots/:botId/calls/user/:userId/logs"
            render={props => (
              <WithAuth botId={props.match.params.botId}>
                <CallsTablePage
                  sourceUrl={callsListUrlGetters.callLogsForUser(props.match.params as any)}
                  fullscreen={hasParam(props.location, 'fullscreen')}
                  botId={props.match.params.botId!}
                />
              </WithAuth>
            )}
          />

          <Route
            path="/bots/:botId/campaigns/:campaignId"
            render={props => (
              <WithAuth botId={props.match.params.botId}>
                <CampaignContactsSheetPage
                  botId={props.match.params.botId!}
                  campaignId={props.match.params.campaignId!}
                />
              </WithAuth>
            )}
          />

          <Route
            path="/bots/:botId/conversations"
            render={(props: any) => (
              <WithAuth botId={props.match.params.botId}>
                <ConversationsTablePage
                  botId={props.match.params.botId!}
                  fullscreen={hasParam(props.location, 'fullscreen')}
                />
              </WithAuth>
            )}
          />

          <Route
            path="/bots/:botId/calls"
            render={props => (
              <WithAuth botId={props.match.params.botId}>
                <CallsTablePage
                  sourceUrl={callsListUrlGetters.callsList(
                    props.match.params as any,
                    props.location.search
                  )}
                  fullscreen={hasParam(props.location, 'fullscreen')}
                  botId={props.match.params.botId!}
                />
              </WithAuth>
            )}
          />

          <Route
            path="/bots/:botId/bot-tester/scenarios"
            render={(props: any) => {
              return (
                <WithAuth botId={props.match.params.botId}>
                  <BotTesterScenariosPage />
                </WithAuth>
              )
            }}
          />

          <Route
            path="/bots/:botId/bot-tester/recorded-runs/scenario/:scenarioId/run/:reversedMicroTimestamp"
            render={(props: any) => {
              const { botId, scenarioId, reversedMicroTimestamp } = props.match.params

              return (
                <WithAuth botId={props.match.params.botId}>
                  <RecordedTesterRunPage
                    descriptor={{ mode: 'scenario', botId, scenarioId, reversedMicroTimestamp }}
                  />
                </WithAuth>
              )
            }}
          />

          <Route
            path="/bots/:botId/bot-tester/recorded-runs/conversation/:userId/run/:reversedMicroTimestamp"
            render={(props: any) => {
              const { botId, userId, reversedMicroTimestamp } = props.match.params

              return (
                <WithAuth botId={props.match.params.botId}>
                  <RecordedTesterRunPage
                    descriptor={{
                      mode: 'conversation',
                      botId,
                      testedUserId: userId,
                      reversedMicroTimestamp
                    }}
                  />
                </WithAuth>
              )
            }}
          />

          <Route
            path="/bots/:botId/wizard"
            render={(props: any) => (
              <WithAuth botId={props.match.params.botId}>
                <WizardPage botId={props.match.params.botId} />
              </WithAuth>
            )}
          />

          <Route
            path="/bots/:botId"
            render={(props: any) => (
              <WithAuth botId={props.match.params.botId}>
                <BotPage botId={props.match.params.botId} />
              </WithAuth>
            )}
          />

          <Route
            exact
            path="/"
            render={() => (
              <WithAuth botId={undefined}>
                <HomePage />
              </WithAuth>
            )}
          />

          <Route
            exact
            path="/voice"
            render={() => (
              <WithAuth botId={undefined}>
                <VoicebuilderPage />
              </WithAuth>
            )}
          />

          <Route exact path="/login" component={Login} />

          <Route path="/marketplace/signup" component={MarketplaceSignupPage} />

          <Route
            path="/users"
            render={() => (
              <WithAuth botId={undefined}>
                <TeamManagementPage />
              </WithAuth>
            )}
          />

          <Redirect to="/login" />
        </Switch>
      </ReactRouter>
    </FeedbotSuspense>
  )
}
